import { useCallback, useSyncExternalStore } from 'react'

/**
 * Theme resolution for the dark-first console.
 *
 * Dark is `:root` in `styles.css`; light is the opt-in `[data-theme='light']`
 * block. The attribute on `<html>` is the single source of truth at runtime —
 * this module writes it, the stylesheet reads it, and nothing else needs to
 * know a theme exists.
 *
 * Three stores, in priority order: an explicit choice in `localStorage`, the
 * OS preference, then dark. Every `localStorage` touch is wrapped: Safari in
 * private mode and a browser with site data blocked both THROW on access
 * rather than returning null, and an operator console that white-screens
 * because someone opened it in a private window is worse than one that
 * forgets a preference.
 */

export type Theme = 'dark' | 'light'

/** The `localStorage` key. Shared with `THEME_INIT_SCRIPT` below so the
 * pre-paint script and the React code can never drift onto two keys. */
export const THEME_STORAGE_KEY = 'theme'

const LIGHT_QUERY = '(prefers-color-scheme: light)'

/**
 * The pre-paint resolver, injected as a blocking inline `<script>` in the
 * document head by `routes/__root.tsx`.
 *
 * It has to be a string of plain JS rather than a React effect: an effect
 * runs after hydration, which is several frames after the browser has
 * already painted the body — and since dark is the default, a light-theme
 * operator would see a full-page black flash on every navigation that
 * reloads the document. Blocking in `<head>` is the only placement that
 * resolves the theme before the first paint.
 *
 * It is also the reason `<html>` carries `suppressHydrationWarning`: the
 * server renders no `data-theme`, this script adds one, and React would
 * otherwise report the attribute it did not write as a hydration mismatch.
 */
export const THEME_INIT_SCRIPT = `
try {
  var stored = window.localStorage.getItem(${JSON.stringify(THEME_STORAGE_KEY)})
  document.documentElement.dataset.theme =
    stored === 'light' || stored === 'dark'
      ? stored
      : window.matchMedia(${JSON.stringify(LIGHT_QUERY)}).matches
        ? 'light'
        : 'dark'
} catch (error) {
  document.documentElement.dataset.theme = 'dark'
}
`.trim()

function isTheme(value: unknown): value is Theme {
  return value === 'dark' || value === 'light'
}

function readStoredTheme(): Theme | null {
  try {
    const stored = window.localStorage.getItem(THEME_STORAGE_KEY)
    return isTheme(stored) ? stored : null
  } catch {
    // Storage is unreadable (private mode, blocked site data). Not an
    // error worth surfacing — it only means "no stored preference".
    return null
  }
}

/** What the theme *should* be, from the stored choice then the OS. */
export function resolveTheme(): Theme {
  const stored = readStoredTheme()
  if (stored !== null) return stored
  return window.matchMedia(LIGHT_QUERY).matches ? 'light' : 'dark'
}

/**
 * The theme currently applied to the document. Reads the attribute rather
 * than re-resolving, so it reports what the user is actually looking at
 * even in the window between the init script and React taking over.
 */
export function getTheme(): Theme {
  if (typeof document === 'undefined') return 'dark'
  const applied = document.documentElement.dataset.theme
  return isTheme(applied) ? applied : resolveTheme()
}

const listeners = new Set<() => void>()

function notify(): void {
  for (const listener of listeners) listener()
}

/** Applies a theme and remembers it. Persisting is best-effort; painting
 * the new theme is not, so the attribute is set either way. */
export function setTheme(theme: Theme): void {
  document.documentElement.dataset.theme = theme
  try {
    window.localStorage.setItem(THEME_STORAGE_KEY, theme)
  } catch {
    // See readStoredTheme: the choice just will not survive a reload.
  }
  notify()
}

function subscribe(onStoreChange: () => void): () => void {
  listeners.add(onStoreChange)

  // A theme set in another tab, and an OS theme flipped while this tab is
  // open. The media listener only changes anything when no explicit choice
  // is stored, because `resolveTheme` prefers the stored one.
  const onStorage = (event: StorageEvent): void => {
    if (event.key !== THEME_STORAGE_KEY) return
    document.documentElement.dataset.theme = resolveTheme()
    onStoreChange()
  }
  const media = window.matchMedia(LIGHT_QUERY)
  const onMediaChange = (): void => {
    if (readStoredTheme() !== null) return
    document.documentElement.dataset.theme = resolveTheme()
    onStoreChange()
  }

  window.addEventListener('storage', onStorage)
  media.addEventListener('change', onMediaChange)
  return () => {
    listeners.delete(onStoreChange)
    window.removeEventListener('storage', onStorage)
    media.removeEventListener('change', onMediaChange)
  }
}

export interface ThemeControl {
  theme: Theme
  setTheme: (theme: Theme) => void
  toggleTheme: () => void
}

/**
 * The hook a theme toggle uses.
 *
 * `useSyncExternalStore` rather than `useState` + `useEffect` because the
 * server cannot know the theme: the server snapshot is the `:root` default
 * (dark) and the client snapshot is the attribute the init script already
 * wrote, which is exactly the split this hook is for. A `useState`
 * initialiser reading `document` would be a hydration mismatch instead.
 */
export function useTheme(): ThemeControl {
  const theme = useSyncExternalStore(subscribe, getTheme, (): Theme => 'dark')
  const toggleTheme = useCallback(() => {
    setTheme(getTheme() === 'dark' ? 'light' : 'dark')
  }, [])
  return { theme, setTheme, toggleTheme }
}
